import { type Dayjs } from "dayjs";
import type Granularity from "src/constants/granularity.enum";
import { formatToApiFormat } from "src/lib/date.helper";

import axiosErrorHandler from "./apiError.handler";
import http from "./axios";
import type IntervalType from "../constants/intervalType.enum";
import type MeasureType from "../constants/measureType.enum";
import { type MeasuresItemizedCompactDto, type GeoDataDirection, type MeasuresSummarizedDto } from "../types/dashboardDatasetDto";
import { type MeasuresItemizedDto } from "../types/locationChartDto";

export async function apiGetLocationDashboard(
  locationIds: string[],
  interval: IntervalType,
  selectedDate: Dayjs | null,
  seriesType: MeasureType,
  isFlowMapEnabled: boolean,
  flowDirection: GeoDataDirection | null,
  lastDays: number | null = null,
  granularity: Granularity | null = null,
  pinned = false,
  fetchParentData = false
): Promise<MeasuresSummarizedDto> {
  try {
    const requestConfig = {
      locationIds: JSON.stringify(locationIds),
      periodInterval: interval,
      seriesType,
      selectedDate: formatToApiFormat(selectedDate),
      isFlowMapEnabled,
      flowDirection,
      lastDays,
      granularity,
      pinned,
      fetchParentData,
    };

    const { data } = await http.get(`Comparison/Summarized`, { params: requestConfig });

    return data;
  } catch (error: unknown) {
    throw axiosErrorHandler(error, "Error while loading location dashboard");
  }
}

export async function apiGetLocationDashboardTab(
  locationIds: string[],
  interval: IntervalType,
  selectedDate: Dayjs | null,
  seriesType: MeasureType,
  lastDays: number | null = null,
  pinned = false
): Promise<MeasuresItemizedCompactDto> {
  try {
    const requestConfig = {
      locationIds: JSON.stringify(locationIds),
      periodInterval: interval,
      seriesType,
      selectedDate: formatToApiFormat(selectedDate),
      lastDays,
      pinned,
    };

    const { data } = await http.get(`Comparison/ItemizedCompact`, { params: requestConfig });

    return data;
  } catch (error: unknown) {
    throw axiosErrorHandler(error, "Error while loading location dashboard");
  }
}

export async function apiGetLocationCharts(
  locationId: string,
  periodInterval: IntervalType,
  selectedDate: Dayjs | null,
  seriesType: MeasureType,
  lastDays: number | null = null,
  isWeather: boolean | null = false,
  isEvents: boolean | null = false
): Promise<MeasuresItemizedDto> {
  try {
    const requestConfig = {
      locationId,
      periodInterval,
      seriesType,
      selectedDate: formatToApiFormat(selectedDate),
      lastDays,
      isWeather,
      isEvents,
    };
    const { data } = await http.get(`Comparison/Itemized`, { params: requestConfig });

    return data;
  } catch (error: unknown) {
    throw axiosErrorHandler(error, "Error while loading location charts");
  }
}
